import { observer } from 'mobx-react-lite'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { useFeatures } from '@/contexts/root-store-context'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import {
  DocumentActionsDropdownContainer,
  DocumentsCardDialogsContainer,
} from '@/features/jobs/details/components/DocumentsCard/DocumentsCardContainer'
import { TxfButtonContainer } from '@/features/jobs/details/components/TxfButton/TxfButtonContainer'
import {
  FileRowSkeleton,
  JobDocumentsSection,
} from '@/features/jobs/details/components/JobDocumentsSection'
import { displayTitle } from '@/features/jobs/details/display-title'

export interface JobDocumentsSectionContainerProps {
  vm: JobDetailsViewModel
}

export const JobDocumentsSectionContainer = observer(
  function JobDocumentsSectionContainer({
    vm,
  }: JobDocumentsSectionContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    const documentsVm = vm.documentsVm
    const documentVms = vm.canViewDocs ? documentsVm.shownDocumentVms : []
    const canAdd =
      vm.canViewDocs && vm.hasAnyDocPermission && !documentsVm.isJobClosed

    return (
      <JobDocumentsSection
        hasDocuments={vm.canViewDocs && documentsVm.documents.length > 0}
        isSkeleton={documentsVm.isFetchingInitial}
        types={documentsVm.filterableDocumentTypes.map((t) => ({
          id: String(t.id),
          name: t.name,
        }))}
        typeFilter={documentsVm.documentTypeFilter}
        onTypeFilterChange={documentsVm.setDocumentTypeFilter}
        query={documentsVm.documentQuery}
        onQueryChange={documentsVm.setDocumentQuery}
        allSelected={documentsVm.areAllShownDocumentsSelected}
        onToggleAll={documentsVm.toggleAllShownDocuments}
        documents={documentVms.map((itemVm) => ({
          id: itemVm.document.id,
          title: itemVm.document.title,
          displayTitle: displayTitle(itemVm.document.title),
          typeName: documentsVm.typeName(itemVm.document),
          isProcessing: itemVm.document.isProcessing,
          ownerName: itemVm.document.owner.displayName,
          dateCreated: itemVm.document.dateCreated,
          selected: itemVm.isSelected,
          onToggleSelected: itemVm.toggleSelected,
          onPress: itemVm.view,
          actions: itemVm.document.downloadable
            ? {
                onView: itemVm.preview,
                onDownload: itemVm.download,
                onUnlink: itemVm.canUnlink ? itemVm.unlink : undefined,
                onDelete: itemVm.canDelete ? itemVm.delete : undefined,
              }
            : null,
        }))}
        documentCount={documentsVm.documentCount}
        selectedCount={documentsVm.selectedDocuments.size}
        canViewDocs={vm.canViewDocs}
        canRemoveDocs={documentsVm.canRemoveDocs}
        isClosed={documentsVm.isJobClosed}
        corporateMode={shellInfo.corporateMode}
        isDownloadSelectedPending={documentsVm.downloadSelected.pending}
        onClearSelection={documentsVm.clearSelection}
        onRemoveSelected={documentsVm.deleteSelected}
        onDownloadSelected={documentsVm.downloadSelected}
        txfButton={
          documentsVm.txfJobVm.canView && (
            <TxfButtonContainer vm={documentsVm.txfJobVm} />
          )
        }
        actionsDropdown={
          canAdd && <DocumentActionsDropdownContainer vm={vm} labelled />
        }
        scrollLoader={
          <SkeletonListInfiniteScrollTrigger
            scroller={documentsVm.scrollProvider}
            maxSkeletons={6}
          >
            <FileRowSkeleton />
          </SkeletonListInfiniteScrollTrigger>
        }
        dialogs={vm.canViewDocs && <DocumentsCardDialogsContainer vm={vm} />}
      />
    )
  },
)
